import React, { Component } from 'react';

import classnames from 'classnames';
import PropTypes from 'prop-types';
import { compose } from 'redux';

import AutopilotCountsContainer from 'common/containers/AutopilotCountsContainer';
import { CompanyContext } from 'common/containers/CompanyContainer';
import OnboardingContainer, {
  asyncFetch as onboardingAsyncFetch,
} from 'common/containers/OnboardingContainer';
import { ViewerContext } from 'common/containers/ViewerContainer';
import asyncConnect from 'common/core/asyncConnect';
import CannyAnnouncementModal from 'common/modals/CannyAnnouncementModal';
import Spinner from 'common/Spinner';
import AdminNav from 'common/subdomain/admin/AdminNav';
import { CollectFeedbackOptionValue } from 'common/subdomain/admin/AdminOnboarding/constants';
import AdminPlanExpired from 'common/subdomain/admin/AdminPlanExpired';
import AdminV2Nav from 'common/subdomain/admin/v2/AdminV2Nav';
import AdminV2PostLimitNotice from 'common/subdomain/admin/v2/AdminV2Notices/AdminV2PostLimitNotice';
import AdminV2TrackedUserNotice from 'common/subdomain/admin/v2/AdminV2Notices/AdminV2TrackedUserNotice';
import AdminV2TrialNotice from 'common/subdomain/admin/v2/AdminV2Notices/AdminV2TrialNotice';
import { dayjs } from 'common/util/dayjsUtils';
import isFree from 'common/util/isFree';
import trackLinkView from 'common/util/trackLinkView';
import withContexts from 'common/util/withContexts';

import AccessContainer from './AccessContainer';
import BoardContainer, { asyncFetch as boardAsyncFetch } from './BoardContainer';
import BoardsContainer from './BoardsContainer';
import ChangelogEntryContainer from './ChangelogEntryContainer';
import ContentContainer from './ContentContainer';
import PostContainer from './PostContainer';
import StripeContainer from './StripeContainer';

import AutopilotAnnouncement from 'img/autopilot.svg';
import IdentifyAnnouncement from 'img/identify-img.png';

import 'css/components/subdomain/admin/_AdminContainer.scss';

class AdminAppContainer extends Component {
  static propTypes = {
    boards: PropTypes.object,
    company: PropTypes.object,
    location: PropTypes.object,
    notifications: PropTypes.object,
    router: PropTypes.object,
    viewer: PropTypes.object,
  };

  componentDidMount() {
    const { company, router } = this.props;

    this.updateBodyOverflow();

    const { viewerIsMember } = company;
    if (viewerIsMember) {
      const { useCase } = company;
      if (!useCase.completed) {
        router.replace('/admin/onboarding/redirect');
        return;
      }

      return;
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.location.pathname !== this.props.location.pathname) {
      this.updateBodyOverflow();
    }
  }

  componentWillUnmount() {
    const html = document.body.parentElement;
    html.style.overflow = '';
    document.body.style.overflow = '';
  }

  updateBodyOverflow() {
    const html = document.body.parentElement;
    if (this.isFullScreenPath()) {
      // Prevents the body from having overflowing elements that cause a scrollbar
      // on pages with fixedHeight. This is to ensure an offscreen portal on these
      // pages doesn't cause an infinite body scroll.
      html.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
    } else {
      html.style.overflow = '';
      document.body.style.overflow = '';
    }
  }

  renderContents() {
    const {
      company,
      location,
      params: { boardURLName, postURLName },
      viewer,
    } = this.props;
    const board = boardURLName ? this.props.boards.items[boardURLName] : null;
    const isLoading = board && board.loading;
    const error = board && board.error;

    const postCouldHaveBeenMoved = postURLName && board?.requestedBoardID;

    if (isLoading) {
      return (
        <ContentContainer className="contentLoading">
          <Spinner />
        </ContentContainer>
      );
    } else if (error) {
      return (
        <ContentContainer outerClassName="notFound">
          Something went wrong, please try again later.
        </ContentContainer>
      );
    } else if (board && board.notFound && !postCouldHaveBeenMoved) {
      return (
        <ContentContainer outerClassName="notFound">
          There is no such board. Did you enter the right URL?
        </ContentContainer>
      );
    } else if (board && board.notAuthorized && !postCouldHaveBeenMoved) {
      return (
        <ContentContainer outerClassName="notAuthorized">
          This board is private, please use an account that can access it.
        </ContentContainer>
      );
    }

    const viewerIsLoggedIn = viewer && viewer._id;
    const BoardSettingsRegex = /^\/admin\/board\/[a-z0-9-]+\/settings/i;
    const isViewingSettings =
      location.pathname.startsWith('/admin/settings') ||
      location.pathname.match(BoardSettingsRegex);

    if (viewerIsLoggedIn && !isViewingSettings) {
      const { billingData, limits, planTrial, stats } = company;

      if (!billingData?.plan?.planID) {
        return <AdminPlanExpired lockoutReason="noPlan" planTrial={planTrial} />;
      }

      const planTrialExpired = !planTrial?.cancelled && planTrial?.expired && !planTrial?.upgraded;
      if (planTrialExpired) {
        return <AdminPlanExpired lockoutReason="trialExpired" planTrial={planTrial} />;
      }

      const { activePosts } = limits;
      const { activePostCount, activePostLimitLockoutAt } = stats;
      const postLimitExceeded = activePosts && activePosts < activePostCount;
      const postLimitLockedOut =
        activePostLimitLockoutAt && new Date(activePostLimitLockoutAt) < new Date();
      if (postLimitExceeded && postLimitLockedOut) {
        return <AdminPlanExpired lockoutReason="activePostLimitLockout" planTrial={planTrial} />;
      }

      const needToDisableFeatures = billingData?.expired;
      if (needToDisableFeatures) {
        return <AdminPlanExpired lockoutReason="extraFeatures" planTrial={planTrial} />;
      }

      const isOverFreeTrackedUserLimit =
        isFree(billingData.plan.planID) &&
        company.limits?.users &&
        company.contributorCount >= company.limits?.users;
      if (isOverFreeTrackedUserLimit) {
        return <AdminPlanExpired lockoutReason="overFreeTrackedUserLimit" planTrial={planTrial} />;
      }
    }

    return this.props.children;
  }

  renderAnnouncements() {
    const { company, location, onboarding, router } = this.props;

    if (!company?.nux) {
      return null;
    }

    const isNUXAutopilotEnabled = !company.nux.autopilot;
    const isNUXIdentifyEnabled = !company.nux.identify && !company.stats.hasUsedIdentify;
    const isInAutopilotSettings = location.pathname.startsWith('/admin/settings/autopilot');
    const isCompanyRecentlyCreated = dayjs().subtract(1, 'day').isBefore(company.created);

    if (
      (!isNUXAutopilotEnabled && !isNUXIdentifyEnabled) ||
      isInAutopilotSettings ||
      isCompanyRecentlyCreated
    ) {
      return null;
    }

    if (isNUXAutopilotEnabled) {
      return (
        <CannyAnnouncementModal
          body={`Never let valuable feedback slip through the cracks again with Canny Autopilot. Automatically detect and consolidate feedback from all your sources in one place.\nConnect a data source to get started.`}
          cta="Enable Autopilot"
          tag="Spotlight"
          image={AutopilotAnnouncement}
          learnMore="https://help.canny.io/en/articles/8202451-beta-inbox"
          name="autopilot"
          onSuccess={() => router.replace('/admin/settings/autopilot')}
          title="Feedback Discovery on Autopilot"
        />
      );
    }

    if (isNUXIdentifyEnabled) {
      let title = 'Turn feedback into revenue insights';
      let body = `Canny Identify helps you to:
        • Enrich user profiles with company, plan, and revenue data
        • Prioritize feedback by seeing which customers drive the most impact
      `;

      if (
        onboarding.publicFeedback ||
        onboarding.motivations?.some(
          (motivation) => motivation === CollectFeedbackOptionValue.Direct,
        )
      ) {
        title = 'Enrich feedback and simplify login';
        body = `Canny Identify helps you:
          • Enrich user profiles with company, plan, and revenue data
          • Automatically authenticate users into your feedback portal

          Teams using Identify see **2.7 more feedback** and **8.7× more engaged users**.`;
      }

      return (
        <CannyAnnouncementModal
          body={body}
          tag="Tip"
          image={IdentifyAnnouncement}
          learnMore="https://help.canny.io/en/articles/1626097-canny-identify"
          name="identify"
          onLearnMoreClick={() => {
            trackLinkView('clicked_identify_announcement', location.pathname, location.search);
          }}
          title={title}
        />
      );
    }
  }

  renderTrialCountdown() {
    const { company } = this.props;
    const { trialingPlan, viewerIsMember } = company;
    if (!viewerIsMember) {
      return null;
    }

    if (trialingPlan) {
      return <AdminV2TrialNotice plan={trialingPlan} />;
    } else {
      return <AdminV2TrackedUserNotice />;
    }
  }

  renderPostLimit() {
    const { limits, stats, viewerIsMember } = this.props.company;
    if (!viewerIsMember) {
      return null;
    }

    return <AdminV2PostLimitNotice limits={limits} stats={stats} />;
  }

  isFullScreenPath() {
    const {
      location: { pathname },
    } = this.props;

    if (
      pathname.startsWith('/admin/changelog') ||
      pathname.startsWith('/admin/feedback') ||
      pathname.startsWith('/admin/autopilot') ||
      pathname.startsWith('/admin/moderation') ||
      pathname.startsWith('/admin/notifications') ||
      pathname.startsWith('/admin/roadmap') ||
      pathname.startsWith('/admin/settings') ||
      pathname.startsWith('/admin/users')
    ) {
      return true;
    }

    return false;
  }

  render() {
    const {
      viewer: { loggedOut },
      company,
    } = this.props;
    if (loggedOut) {
      return null;
    }

    const isIDBEnabled = company?.featureAllowlist?.includes('idea-database');
    const className = classnames('adminContainer', {
      fixedHeight: this.isFullScreenPath(),
    });

    return (
      <StripeContainer>
        <AccessContainer>
          <OnboardingContainer>
            <div className={className}>
              <div className="adminContentsContainer">
                {this.renderTrialCountdown()}
                {this.renderAnnouncements()}
                {this.renderPostLimit()}
                <div
                  className={classnames('adminContents', {
                    v2: isIDBEnabled,
                  })}>
                  <BoardsContainer>
                    <BoardContainer>
                      <PostContainer>
                        <ChangelogEntryContainer>
                          <AutopilotCountsContainer>
                            {isIDBEnabled ? (
                              <AdminV2Nav notifications={this.props.notifications} />
                            ) : (
                              <AdminNav notifications={this.props.notifications} />
                            )}
                          </AutopilotCountsContainer>
                        </ChangelogEntryContainer>
                      </PostContainer>
                    </BoardContainer>
                  </BoardsContainer>
                  {this.renderContents()}
                </div>
              </div>
            </div>
          </OnboardingContainer>
        </AccessContainer>
      </StripeContainer>
    );
  }
}

export default compose(
  asyncConnect([BoardsContainer.asyncConnect, onboardingAsyncFetch, boardAsyncFetch], (state) => ({
    boards: state.boards,
    notifications: state.notifications,
    onboarding: state.onboarding.data,
  })),
  withContexts(
    {
      company: CompanyContext,
      viewer: ViewerContext,
    },
    {
      forwardRef: true,
    },
  ),
)(AdminAppContainer);
